import "@/App.css";
import "@/i18n";
import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { SettingsProvider } from "@/context/SettingsContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { BrandingHead } from "@/components/layout/BrandingHead";
import { PageLoader } from "@/components/ui/States";
import LangRedirect from "@/pages/LangRedirect";

// Home stays eager — it's the landing page, blocking its parse on a network
// round-trip would defeat the LCP work. Everything else is split into its own
// chunk so a /en visitor only downloads the home JS, a /services visitor only
// downloads the services chunk, etc. webpackPrefetch tells the browser to
// fetch these chunks during idle time after Home is interactive, so clicking
// "Services" / "About" / "Contact" in the nav feels instant.
import Home from "@/pages/Home";
const Services = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Services"));
const ServiceDetail = lazy(() => import(/* webpackPrefetch: true */ "@/pages/ServiceDetail"));
const About = lazy(() => import(/* webpackPrefetch: true */ "@/pages/About"));
const Contact = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Contact"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Login = lazy(() => import("@/pages/Login"));

// Admin layout/guard + child managers are code-split (heavy: TipTap)
const AdminApp = lazy(() => import("@/admin/AdminApp"));
const Dashboard = lazy(() => import("@/admin/pages/Dashboard"));
const PagesManager = lazy(() => import("@/admin/pages/PagesManager"));
const PageEditor = lazy(() => import("@/admin/pages/PageEditor"));
const ServicesManager = lazy(() => import("@/admin/pages/ServicesManager"));
const TeamManager = lazy(() => import("@/admin/pages/TeamManager"));
const TimelineManager = lazy(() => import("@/admin/pages/TimelineManager"));
const AwardsManager = lazy(() => import("@/admin/pages/AwardsManager"));
const MessagesManager = lazy(() => import("@/admin/pages/MessagesManager"));
const SettingsManager = lazy(() => import("@/admin/pages/SettingsManager"));

function App() {
  return (
    <SettingsProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrandingHead />
          <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<LangRedirect />} />
              <Route path="/login" element={<Login />} />

              {/* Admin — explicit static routes so they outrank /:lang/* (RRv6 ranking) */}
              <Route path="/admin" element={<AdminApp />}>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="pages" element={<PagesManager />} />
                <Route path="pages/:slug" element={<PageEditor />} />
                <Route path="services" element={<ServicesManager />} />
                <Route path="team" element={<TeamManager />} />
                <Route path="timeline" element={<TimelineManager />} />
                <Route path="awards" element={<AwardsManager />} />
                <Route path="messages" element={<MessagesManager />} />
                <Route path="settings" element={<SettingsManager />} />
                <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
              </Route>

              <Route path="/:lang" element={<PublicLayout />}>
                <Route index element={<Home />} />
                <Route path="services" element={<Services />} />
                <Route path="services/:slug" element={<ServiceDetail />} />
                <Route path="about" element={<About />} />
                <Route path="contact" element={<Contact />} />
                <Route path="privacy" element={<Privacy />} />
                <Route path="*" element={<NotFound />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" />
        </AuthProvider>
      </ThemeProvider>
    </SettingsProvider>
  );
}

export default App;
